import React, { useCallback, useEffect, useRef } from "react";

import { faArrowLeft, faImages } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link, useParams } from "react-router-dom";

import LoadingEvent from "../components/Dashboard/LoadingEvent";
import Header from "../components/Header/Header";
import PhotoSlider from "../components/PhotoSlider/PhotoSlider";
import { useLanguage } from "../contexts/LanguageContext";
import apiService from "../services/apiService";

import "./EventPhotosGuest.css";

const EventPhotosGuest = () => {
  const { eventId } = useParams();
  const { t } = useLanguage();
  const PHOTOS_PER_PAGE = 20;
  const [photos, setPhotos] = React.useState([]);
  const [event, setEvent] = React.useState(null);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [eventsLoading, setEventsLoading] = React.useState(true);
  const [isFetchingMore, setIsFetchingMore] = React.useState(false);
  const [hasMore, setHasMore] = React.useState(true);
  const [sliderOpen, setSliderOpen] = React.useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = React.useState(0);
  const sentinelRef = useRef(null);
  const fetchInProgressRef = useRef(false);

  const handlePhotoClick = (index) => {
    setCurrentPhotoIndex(index);
    setSliderOpen(true);
  };

  const loadPhotosPage = useCallback(
    async (page) => {
      if (!eventId || fetchInProgressRef.current) return;
      fetchInProgressRef.current = true;
      if (page > 1) {
        setIsFetchingMore(true);
      }
      try {
        const response = await apiService.getEventPhotos(
          eventId,
          page - 1,
          PHOTOS_PER_PAGE
        );
        if (response.success) {
          const fetchedPhotos = response.photos || [];
          const totalCount = response.pagination.total;
          const calculatedTotalPages = Math.ceil(totalCount / PHOTOS_PER_PAGE);

          setEventsLoading(false);
          setPhotos((prevPhotos) =>
            page === 1 ? fetchedPhotos : [...prevPhotos, ...fetchedPhotos]
          );
          setCurrentPage(page);
          setHasMore(page < calculatedTotalPages);
          return fetchedPhotos;
        } else {
          console.error("Error loading photos:", response.message);
          if (page === 1) {
            setPhotos([]);
          }
          setHasMore(false);
          return [];
        }
      } catch (error) {
        console.error("Error loading photos:", error);
        if (page === 1) {
          setPhotos([]);
        }
        setHasMore(false);
        return [];
      } finally {
        if (page > 1) {
          setIsFetchingMore(false);
        }
        fetchInProgressRef.current = false;
        setEventsLoading(false);
      }
    },
    [eventId]
  );

  useEffect(() => {
    setPhotos([]);
    setCurrentPage(1);
    setHasMore(true);
    setEventsLoading(true);
    loadPhotosPage(1);
  }, [eventId, loadPhotosPage]);

  useEffect(() => {
    let cancelled = false;
    apiService
      .getEvent(eventId)
      .then((eventData) => {
        if (!cancelled) setEvent(eventData);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  useEffect(() => {
    if (!hasMore || eventsLoading) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          loadPhotosPage(currentPage + 1);
        }
      },
      { rootMargin: "200px" }
    );

    const sentinel = sentinelRef.current;
    if (sentinel) {
      observer.observe(sentinel);
    }

    return () => {
      if (sentinel) {
        observer.unobserve(sentinel);
      }
    };
  }, [hasMore, eventsLoading, currentPage, loadPhotosPage]);

  if (eventsLoading) {
    return <LoadingEvent message={t("commons.loadingPhotos")} />;
  }

  return (
    <div className="event-photos-guest">
      <Header fullHeader={false} showLanguageSelector />
      <section className="guest-gallery-hero">
        <Link to={`/event/${eventId}`} className="guest-gallery-back">
          <FontAwesomeIcon icon={faArrowLeft} />
          {t("eventView.shareYourPhotos")}
        </Link>
        <div>
          <p>{t("eventView.eventPhotos")}</p>
          <h1>{event?.name || t("eventView.eventPhotos")}</h1>
          <span><FontAwesomeIcon icon={faImages} /> {photos.length} {t("commons.photos")}</span>
        </div>
      </section>
      <div className="dashboard-layout">
        {photos.length > 0 ? (
          <div className="photo-grid">
            {photos.map((photo, index) => (
              <button key={photo.id} className="photo-item" onClick={() => handlePhotoClick(index)} aria-label={`${t("eventView.eventPhotos")} ${index + 1}`}>
                <img src={photo.url} alt={photo.description || "Event moment"} />
                <span>{String(index + 1).padStart(2, "0")}</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="guest-gallery-empty"><FontAwesomeIcon icon={faImages} /><h2>{t("notPhotosError.title")}</h2></div>
        )}
        <div ref={sentinelRef} />
        {isFetchingMore && (
          <div className="loading-indicator">{t("commons.loadingPhotos")}</div>
        )}
      </div>
      <PhotoSlider
        photos={photos}
        initialIndex={currentPhotoIndex}
        isOpen={sliderOpen}
        onClose={() => setSliderOpen(false)}
      />
    </div>
  );
};

export default EventPhotosGuest;
